import { FC, Fragment } from "preact/compat";
import { useEffect } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useApp } from "~/core/store";
import { IconButton } from "~/components";
import Record from "./Record";
import PrevIcon from "@material-design-icons/svg/outlined/chevron_left.svg";
import NextIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import CloseIcon from "@material-design-icons/svg/outlined/close.svg";

const ITEM_WIDTH = 420;
const ITEM_GAP_X = 20;
const ITEMS_COUNT = 3;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 26;

const showTooltip = (target: HTMLElement) => {
    if (target.dataset.djTooltip) {
        return;
    }

    const rect = target.getBoundingClientRect();
    const element = document.createElement("div");
    let y = rect.top - TOOLTIP_HEIGHT - 4;
    let x = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;

    if (y < 0) {
        y = rect.bottom + 4;
        element.style.animationName = "tooltip-down";
    }

    if (x < 0) {
        x = 4;
    }

    element.classList.add("dj-tooltip");

    element.style.top = `${y}px`;
    element.style.left = `${x}px`;
    element.textContent = "Actions have no effect in journey mode";

    target.dataset.djTooltip = "true";
    document.body.appendChild(element);

    element.addEventListener("animationend", () => {
        element.remove();
        delete target.dataset.djTooltip;
    });
};

const DiscoveryHistory: FC = () => {
    const { help } = useApp();

    useEffect(() => {
        if (!help.isDiscovery.value) {
            return;
        }

        const handleClick = (e: MouseEvent) => {
            if (!(e.target instanceof Element)) {
                return;
            }

            const dtp = e.target.closest(".dtp");

            if (!dtp) {
                return;
            }

            if (e.target.closest("[data-journey-pass]")) {
                return;
            }

            const t = e.target.closest<HTMLElement>(
                "button, a, input, textarea, select, [role=button], label",
            );

            if (t) {
                showTooltip(t);
            }

            e.preventDefault();
            e.stopPropagation();
        };

        const handleDblClick = (e: MouseEvent) => {
            if (!(e.target instanceof Element)) {
                return;
            }

            if (e.target.nodeName === "CANVAS") {
                e.preventDefault();
                e.stopPropagation();

                const parent = e.target.closest("div");

                if (parent) {
                    showTooltip(parent);
                }
            }
        };

        window.addEventListener("click", handleClick, true);
        window.addEventListener("dblclick", handleDblClick, true);

        return () => {
            window.removeEventListener("click", handleClick, true);
            window.removeEventListener("dblclick", handleDblClick, true);
        };
    }, [help.isDiscovery.value]);

    const hasHistory = help.history.value.length > 0;
    const activeIdx = help.activeHistoryIndex.value;
    const count = help.history.value.length;
    const fullWidth = ITEM_WIDTH * count + ITEM_GAP_X * (count - 1);
    const maxWidth = ITEM_WIDTH * ITEMS_COUNT + ITEM_GAP_X * (ITEMS_COUNT - 1);
    const translateX = (activeIdx - 1) * -(ITEM_WIDTH + ITEM_GAP_X);

    return (
        <Transition
            show={hasHistory}
            as={Fragment}
            enter="transition duration-200"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
        >
            <div className="fixed bottom-4 left-1/2 z-[101] flex -translate-x-1/2 flex-col items-start">
                <div
                    style={{
                        width: ITEM_WIDTH,
                        transform: `translateX(${ITEM_WIDTH + ITEM_GAP_X}px)`,
                    }}
                    className="mb-4 flex items-center justify-between rounded-3xl border border-gray-300 bg-white p-4 shadow-lg"
                >
                    <div className="flex items-center gap-x-2">
                        <IconButton
                            disabled={activeIdx === 0}
                            title="Previous manual"
                            onClick={() => help.navigateHistory(-1)}
                        >
                            <PrevIcon width="20" height="20" />
                        </IconButton>
                        <span className="inline-block min-w-[50px] text-center text-lg tracking-wider">
                            {activeIdx + 1}/{count}
                        </span>
                        <IconButton
                            disabled={activeIdx === count - 1}
                            title="Next manual"
                            onClick={() => help.navigateHistory(1)}
                        >
                            <NextIcon width="20" height="20" />
                        </IconButton>
                    </div>
                    <IconButton
                        title="Close"
                        onClick={() => help.closeHistory()}
                    >
                        <CloseIcon width="20" height="20" />
                    </IconButton>
                </div>
                <div
                    style={{ width: maxWidth, maxWidth }}
                    className="relative h-[100px] overflow-hidden"
                >
                    <div
                        style={{
                            width: fullWidth,
                            columnGap: ITEM_GAP_X,
                            transform: `translateX(${translateX}px)`,
                        }}
                        className="absolute left-0 top-[5px] flex transition duration-200"
                    >
                        {help.history.value.map((item, index) => (
                            <Record
                                key={item.tag}
                                record={item}
                                index={index}
                                width={ITEM_WIDTH}
                                active={index === activeIdx}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </Transition>
    );
};

export default DiscoveryHistory;
